import React, { useEffect, useState } from 'react';
import {
  Container,
  Stack,
  Typography,
  useTheme,
  Box,
  Tab,
  Tabs,
  tabClasses,
  styled,
  Card,
  Divider,
  Tooltip,
  MenuItemProps,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  IconButton,
  CardActionArea,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Rating,
} from '@mui/material';
import { fetchGet, fetchPost } from '../../../api/common';
import {
  CustomQuestion,
  TableRowType,
} from '@par/shared/types/custom-questions';
import { Profile } from '@par/shared/types/player';
import { QuestionStatsDialog } from '../../../dialog/QuestionStats';
import { CustomQuestionsEditDialog } from '../../../dialog/CustomQuestionsEditDialog';
import { useSnackbar } from 'notistack';
import { getAvgScoreGraphic, getScoreFromStats, getSeenCount } from './helper';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BoltIcon from '@mui/icons-material/Bolt';
import { average, utcToLocalDate } from '../../room/utils';
import { timeSince } from '@par/shared/utils/date-time';
import {
  Search,
  SearchDrawer,
  SearchIconWrapper,
  StyledInputBase,
} from '../../bottom-nav/SearchDrawer';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { CQTable } from './CQTable';
import ClearIcon from '@mui/icons-material/Clear';
import GradingIcon from '@mui/icons-material/Grading';
import TimerIcon from '@mui/icons-material/Timer';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import { getBarChart } from '../../../common/components/QuestionStatsBarChart';
import { QuestionStats } from '@par/shared/types/question-stats';
import { CgAsterisk } from 'react-icons/cg';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate, useParams } from 'react-router-dom';

const tabLabels = [
  '',
  'A&A',
  'Biz',
  'ClsM',
  'Cult',
  'F&D',
  'Film',
  'Geo',
  'His',
  'Lit',
  'Math',
  'Nich',
  'P&C',
  'P&R',
  'PopM',
  'Sci',
  'Sprt',
  'Thtr',
  'TV',
  'V&L',
  'VG',
];

// the order of these should be synced with tabLables
const categories = [
  '',
  'Art & Architecture',
  'Business',
  'Classical Music',
  'Culture',
  'Food & Drink',
  'Film',
  'Geography',
  'History',
  'Literature',
  'Math',
  'Niche',
  'Politics & Civics',
  'Philosophy & Religion',
  'Popular Music',
  'Science',
  'Sports',
  'Theater',
  'TV',
  'Vocab & Language',
  'Video Games',
];

const isBoostedByDate = (timestamp: number) => {
  const currentTimestamp = new Date().getTime();
  const twoWeeksAgoTimestamp = currentTimestamp - 14 * 24 * 60 * 60 * 1000; // 14 days, 24 hours, 60 minutes, 60 seconds, 1000 milliseconds
  return timestamp >= twoWeeksAgoTimestamp;
};

const isBoostedBySeenCount = (question: CustomQuestionStats) =>
  (question.stats?.seen || 0) < 10;

export interface CustomQuestionStats extends CustomQuestion {
  stats: QuestionStats;
}

const NarrowTab = styled(Tab)(({ theme }) => ({
  [`&.${tabClasses.root}`]: {
    height: '70px',
    borderTop: '1px solid',
    borderColor: 'gray',
    borderRadius: '10px 10px 0 0',
    marginRight: '5px',
    minWidth: '50px',
    width: '50px',
    '&:hover': {
      borderColor: 'white',
    },
  },
  '&.Mui-selected': {
    backgroundColor: '#622626',
    borderColor: theme.palette.primary.main,
    color: '#ffe300',
  },
}));

type PropTypes = {
  profile: Profile;
};

export const CQDashboard = ({ profile }: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { profileId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [customQuestions, setCustomQuestions] =
    useState<CustomQuestionStats[]>();
  const [filteredCustomQuestions, setFilteredCustomQuestions] =
    useState<CustomQuestionStats[]>();
  const [searchedCustomQuestions, setSearchedCustomQuestions] =
    useState<CustomQuestionStats[]>();
  const [value, setValue] = useState(0);
  const [clickedQuestion, setClickedQuestion] = useState<CustomQuestionStats>();
  const [openQuestionStatsDialog, setOpenQuestionStatsDialog] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [cardView, setCardView] = useState<'card' | 'list'>(
    profile.customQuestionsSettings?.listView ? 'list' : 'card'
  );
  const [questionStatus, setQuestionStatus] = useState<
    'approved' | 'pending' | 'rejected' | 'disabled'
  >('approved');
  const [customQuestionsApproved, setCustomQuestionsApproved] =
    useState<CustomQuestionStats[]>();
  const [customQuestionsInReview, setCustomQuestionsInReview] =
    useState<CustomQuestionStats[]>();
  const [customQuestionsRejected, setCustomQuestionsRejected] =
    useState<CustomQuestionStats[]>();
  const [customQuestionsDisabled, setCustomQuestionsDisabled] =
    useState<CustomQuestionStats[]>();

  useEffect(() => {
    if (!profileId || profileId !== profile.id) {
      navigate('/');
    }
  }, [profileId]);

  useEffect(() => {
    fetchCustomQuestions();
  }, [profile]);

  useEffect(() => {
    if (!customQuestions) return;
    if (searchTerm.length > 2 && searchedCustomQuestions) {
      if (value === 0) {
        const searchedCat = searchedCustomQuestions.filter(
          (cq) => isBoostedByDate(cq.approvedAt) || isBoostedBySeenCount(cq)
        );
        setFilteredCustomQuestions(searchedCat);
        return;
      } else {
        const searchedCat = searchedCustomQuestions.filter(
          (cq) => cq.category === categories[value]
        );
        setFilteredCustomQuestions(searchedCat);
        return;
      }
    }
    updateFilterBasedOnQuestionStatus();
  }, [value]);

  useEffect(() => {
    if (!customQuestions) return;
    if (!searchTerm.length) {
      updateFilterBasedOnQuestionStatus();
      return;
    }
    if (searchTerm.length < 3) return;
    debouncedSearch(searchTerm);
  }, [searchTerm]);

  const debouncedSearch = debounce((term: string) => {
    searchQuestions(term);
  }, 300);

  const searchQuestions = (term: string) => {
    const questionSet =
      questionStatus === 'approved'
        ? customQuestionsApproved
        : questionStatus === 'pending'
        ? customQuestionsInReview
        : questionStatus === 'disabled'
        ? customQuestionsDisabled
        : customQuestionsRejected;
    const searched = questionSet.filter(
      (cq) =>
        cq.question.toLowerCase().includes(term.toLowerCase()) ||
        cq.answer.toLowerCase().includes(term.toLowerCase())
    );
    setSearchedCustomQuestions(searched);
    if (value === 0) {
      const filtered = searched.filter(
        (cq) => isBoostedByDate(cq.approvedAt) || isBoostedBySeenCount(cq)
      );
      setFilteredCustomQuestions(filtered);
      return;
    }
    const filtered = searched.filter((cq) => cq.category === categories[value]);
    setFilteredCustomQuestions(filtered);
  };

  const handleClearSearchClick = () => {
    setSearchTerm('');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const fetchCustomQuestions = async () => {
    const res = await fetchGet(`/custom-questions/user/${profile.id}`);
    setCustomQuestions(res);
    const approvedQuestions: CustomQuestionStats[] = res.filter(
      (q) => !!q.approved
    );
    setCustomQuestionsApproved(approvedQuestions);
    const pendingQuestions = res.filter(
      (q) => q.approved === undefined || q.approved === null
    );
    setCustomQuestionsInReview(pendingQuestions);
    const rejectedQuestions = res.filter((q) => q.approved === false);
    setCustomQuestionsRejected(rejectedQuestions);
    const disabledQuestions = res.filter((q) => q.exclude);
    setCustomQuestionsDisabled(disabledQuestions);
    const filteredCustomQuestions = approvedQuestions.filter(
      (cq) => isBoostedByDate(cq.approvedAt) || isBoostedBySeenCount(cq)
    );
    setFilteredCustomQuestions(filteredCustomQuestions);
  };

  const handleDetailsClick = (q: TableRowType | CustomQuestionStats) => {
    const found = customQuestions.find((cq) => cq._id === q._id);
    setClickedQuestion(found);
    setOpenQuestionStatsDialog(true);
  };

  const handleSubmitEdit = async (questionId: string, editedQuestion: any) => {
    if (editedQuestion.alternateAnswers) {
      editedQuestion.alternateAnswers = editedQuestion.alternateAnswers
        .split(',')
        .map((a) => a.trim());
    }
    if (editedQuestion.promptAnswer) {
      editedQuestion.promptAnswer = editedQuestion.promptAnswer
        .split(',')
        .map((a) => a.trim());
    }
    const body = {
      profileId: profile.id,
      questionId,
      editedQuestion,
    };
    const res = await fetchPost('/custom-questions/edit-question', body);
    setOpenEditModal(false);
    enqueueSnackbar(`Question edited`, { variant: 'success' });
    await fetchCustomQuestions();
  };

  const getCQCount = (index: number) => {
    if (!customQuestions) return;
    if (searchTerm.length > 2 && searchedCustomQuestions) {
      if (index === 0) {
        return searchedCustomQuestions.filter(
          (cq) => isBoostedByDate(cq.approvedAt) || isBoostedBySeenCount(cq)
        ).length;
      } else {
        return searchedCustomQuestions.filter(
          (cq) => cq.category === categories[index]
        ).length;
      }
    }

    if (questionStatus === 'rejected') {
      if (index === 0) {
        return customQuestionsRejected.length;
      } else {
        return customQuestionsRejected.filter(
          (cq) => cq.category === categories[index]
        ).length;
      }
    }
    if (questionStatus === 'pending') {
      if (index === 0) {
        return customQuestionsInReview.length;
      } else {
        return customQuestionsInReview.filter(
          (cq) => cq.category === categories[index]
        ).length;
      }
    }
    if (questionStatus === 'disabled') {
      if (index === 0) {
        return customQuestionsDisabled.length;
      } else {
        return customQuestionsDisabled.filter(
          (cq) => cq.category === categories[index]
        ).length;
      }
    }
    if (questionStatus === 'approved') {
      if (index === 0) {
        return customQuestionsApproved.filter(
          (cq) => isBoostedByDate(cq.approvedAt) || isBoostedBySeenCount(cq)
        ).length;
      } else {
        return customQuestionsApproved.filter(
          (cq) => cq.category === categories[index]
        ).length;
      }
    }
  };

  const customQuestionCard = (question: CustomQuestionStats) => {
    return (
      <Stack
        direction="row"
        sx={{
          backgroundColor: question.exclude ? '#666666' : 'inherit',
        }}
      >
        <Stack sx={{ padding: 2 }}>
          <Stack direction="row" mb={1}>
            <Stack
              direction="row"
              spacing={1}
              width="80%"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography sx={{ fontSize: 14 }} color="text.secondary">
                {question.category}
              </Typography>
              <Tooltip
                title={`Approved on: ${utcToLocalDate(question.approvedAt)}`}
                placement="top"
              >
                <AccessTimeIcon sx={{ fontSize: '20px' }} />
              </Tooltip>
              {isBoostedByDate(question.approvedAt) ||
              isBoostedBySeenCount(question) ? (
                <Tooltip title="Boosted" placement="top">
                  <BoltIcon sx={{ color: 'yellow' }} />
                </Tooltip>
              ) : null}
              {question.stats?.stars &&
              (question.stats.stars.length >= 5 ||
                (question.stats.stars.length > 0 &&
                  question.stats.seen >= 10)) ? (
                <Tooltip
                  title={`Rated by ${question.stats.stars.length} players`}
                  placement="top"
                >
                  <Box display="inline-block">
                    <Rating
                      max={3}
                      precision={0.1}
                      value={average(
                        question.stats?.stars?.map((s) => s.rating)
                      )}
                      readOnly
                    />
                  </Box>
                </Tooltip>
              ) : null}
            </Stack>
            <Box width="20%">
              <Typography
                sx={{
                  fontSize: 14,
                  float: 'right',
                }}
                fontWeight={600}
                color="text.secondary"
              >
                PAR {question.par}
              </Typography>
            </Box>
          </Stack>
          <Typography
            variant="body2"
            mb={1}
            sx={{
              width: '350px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {question.question}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.dailies.main }}
            mb={1}
          >
            {question.answer}
          </Typography>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack sx={{ padding: 2 }}>
          {question.rejectionComment ? (
            <Stack spacing={1}>
              <Typography variant="caption" color="error.main">
                Rejection Reason
              </Typography>
              <Tooltip title={question.rejectionComment} placement="top">
                <Typography variant="caption">
                  {question.rejectionComment.slice(0, 20)}
                  {question.rejectionComment.length > 20 ? ' ..' : ''}
                </Typography>
              </Tooltip>
            </Stack>
          ) : question.approved === null ? (
            <Button onClick={() => handleEditClick(question)}>
              Click to edit
            </Button>
          ) : question.stats ? (
            <Stack
              spacing={1}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography>
                {getAvgScoreGraphic(
                  theme,
                  getScoreFromStats(question),
                  question.par
                )}
              </Typography>
              <Stack
                direction={'row'}
                spacing={1}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Typography variant="caption">Seen by</Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {getSeenCount(question.stats)}
                </Typography>
              </Stack>
              <Box
                sx={{
                  height: 40,
                }}
              >
                {getBarChart(question.stats)}
              </Box>
            </Stack>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle2">Not seen</Typography>
            </Box>
          )}
        </Stack>
      </Stack>
    );
  };

  const handleSearchInput = (event: React.FormEvent<HTMLDivElement>) => {
    setSearchTerm(event.target['value']);
  };

  const handleCardViewToggle = async (
    event: React.MouseEvent<HTMLElement>,
    nextView: 'card' | 'list'
  ) => {
    if (nextView === null) return;
    setCardView(nextView);
    await fetchPost('/profile/custom-questions-settings', {
      profileId: profile?.id,
      listView: nextView === 'list',
    });
  };

  const updateFilterBasedOnQuestionStatus = () => {
    if (questionStatus === 'rejected') {
      if (value === 0) {
        setFilteredCustomQuestions(customQuestionsRejected);
        return;
      } else {
        const filteredRejected = customQuestionsRejected.filter(
          (cq) => cq.category === categories[value]
        );
        setFilteredCustomQuestions(filteredRejected);
        return;
      }
    }
    if (questionStatus === 'pending') {
      if (value === 0) {
        setFilteredCustomQuestions(customQuestionsInReview);
        return;
      } else {
        const filteredInReview = customQuestionsInReview.filter(
          (cq) => cq.category === categories[value]
        );
        setFilteredCustomQuestions(filteredInReview);
        return;
      }
    }
    if (questionStatus === 'disabled') {
      if (value === 0) {
        setFilteredCustomQuestions(customQuestionsDisabled);
        return;
      } else {
        const filteredInReview = customQuestionsDisabled.filter(
          (cq) => cq.category === categories[value]
        );
        setFilteredCustomQuestions(filteredInReview);
        return;
      }
    }
    if (questionStatus === 'approved') {
      if (value === 0) {
        const filteredApproved = customQuestionsApproved.filter(
          (cq) => isBoostedByDate(cq.approvedAt) || isBoostedBySeenCount(cq)
        );
        setFilteredCustomQuestions(filteredApproved);
        return;
      } else {
        const filteredApproved = customQuestionsApproved.filter(
          (cq) => cq.category === categories[value]
        );
        setFilteredCustomQuestions(filteredApproved);
        return;
      }
    }
  };

  useEffect(() => {
    if (!customQuestions) return;
    if (searchTerm.length > 2) {
      searchQuestions(searchTerm);
      return;
    }
    updateFilterBasedOnQuestionStatus();
  }, [questionStatus]);

  const handlePendingOrInReviewToggle = (
    event: React.MouseEvent<HTMLElement>,
    nextView: 'approved' | 'pending' | 'rejected' | 'disabled' | null
  ) => {
    if (nextView === null) return;
    setQuestionStatus(nextView);
  };

  const handleCardClick = (q: CustomQuestionStats) => {
    if (q.approved === null) return; // not a good way to prevevnt opening both the question view modal and edit modal
    handleDetailsClick(q);
  };

  const handleEditClick = (q: CustomQuestionStats) => {
    setClickedQuestion(q);
    setOpenEditModal(true);
  };

  return (
    <Container>
      {clickedQuestion ? (
        <QuestionStatsDialog
          onClose={() => setOpenQuestionStatsDialog(false)}
          open={openQuestionStatsDialog}
          profileId={profile.id}
          question={clickedQuestion}
        />
      ) : null}
      {clickedQuestion ? (
        <CustomQuestionsEditDialog
          close={() => setOpenEditModal(false)}
          customQuestion={clickedQuestion}
          formType="edit"
          handleSubmitEdit={handleSubmitEdit}
          open={openEditModal}
          questionId={clickedQuestion._id}
        />
      ) : null}
      <Stack>
        {/* <Typography variant="h4" textAlign={'center'} letterSpacing={20}>
          <Typography
            variant="h6"
            textAlign={'center'}
            letterSpacing={5}
            mb={1}
          >
            {profile.username}'s
          </Typography>
          CUSTOM QUESTIONS
        </Typography> */}
        {searchTerm?.length > 2 ? (
          <Typography variant="subtitle2" ml={1} color="ranked.main">
            Search results: {searchedCustomQuestions?.length}
          </Typography>
        ) : questionStatus === 'pending' ? (
          <Typography variant="subtitle2" color="warning.main" ml={1}>
            Questions in review: {customQuestionsInReview?.length}
          </Typography>
        ) : questionStatus === 'rejected' ? (
          <Typography variant="subtitle2" color="error.main" ml={1}>
            Rejected questions: {customQuestionsRejected?.length}
          </Typography>
        ) : questionStatus === 'disabled' ? (
          <Typography variant="subtitle2" ml={1}>
            Disabled questions: {customQuestionsDisabled?.length}
          </Typography>
        ) : (
          <Typography variant="subtitle2" ml={1}>
            Approved questions: {customQuestionsApproved?.length}
          </Typography>
        )}
        <Stack direction={'row'} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box width={'25%'}>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search (min 3 chars)"
                inputProps={{ 'aria-label': 'search' }}
                value={searchTerm}
                onInput={(event) => handleSearchInput(event)}
                endAdornment={
                  searchTerm.length > 2 ? (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{ marginRight: 0 }}
                        aria-label="search filters"
                        edge="end"
                        onClick={handleClearSearchClick}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              />
            </Search>
          </Box>
          <Stack
            direction={'row'}
            spacing={2}
            width={'75%'}
            sx={{
              display: 'flex',
              justifyContent: 'right',
            }}
          >
            <ToggleButtonGroup
              value={questionStatus}
              exclusive
              onChange={handlePendingOrInReviewToggle}
              size="small"
            >
              <ToggleButton value="approved" aria-label="approved">
                <Tooltip title="View approved questions" placement="top">
                  <GradingIcon color="success" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="pending" aria-label="pending">
                <Tooltip title="View questions in-review" placement="top">
                  <TimerIcon color="warning" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="rejected" aria-label="rejected">
                <Tooltip title="View rejected questions" placement="top">
                  <DoDisturbIcon color="error" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="disabled" aria-label="disabled">
                <Tooltip title="View disabled questions" placement="top">
                  <RemoveIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              value={cardView}
              exclusive
              onChange={handleCardViewToggle}
              size="small"
            >
              <ToggleButton value="card" aria-label="card">
                <Tooltip title="Card view" placement="top">
                  <ViewModuleIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list" aria-label="list">
                <Tooltip title="List view" placement="top">
                  <ViewListIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={5} mt={2}>
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Tabs value={value} onChange={handleTabChange}>
                {tabLabels.map((l, i) => (
                  <NarrowTab
                    label={
                      <Stack spacing={1}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{
                            paddingX: 1,
                            lineHeight: 1.2,
                            backgroundColor: 'black',
                            border: '1px solid',
                            borderColor:
                              searchTerm.length > 2 && getCQCount(i) > 0
                                ? 'ranked.main'
                                : '#624e00',
                            borderRadius:
                              searchTerm.length > 2 && getCQCount(i) > 0
                                ? 0
                                : '10px',
                            color:
                              questionStatus === 'approved' && getCQCount(i) > 0
                                ? 'success.main'
                                : questionStatus === 'pending' &&
                                  getCQCount(i) > 0
                                ? 'warning.main'
                                : questionStatus === 'rejected' &&
                                  getCQCount(i) > 0
                                ? 'error.main'
                                : getCQCount(i) === 0
                                ? 'gray'
                                : 'none',
                          }}
                        >
                          {getCQCount(i)}
                        </Typography>
                        {i === 0 ? (
                          <Tooltip
                            title={`${
                              questionStatus === 'approved'
                                ? 'All boosted questions'
                                : questionStatus === 'pending'
                                ? 'All questions in review'
                                : 'All rejected questions'
                            }`}
                          >
                            <Box
                              sx={{ display: 'flex', justifyContent: 'center' }}
                            >
                              {questionStatus === 'approved' ? (
                                <BoltIcon sx={{ color: 'yellow' }} />
                              ) : (
                                <CgAsterisk style={{ fontSize: '24px' }} />
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="subtitle2" fontWeight={600}>
                            {l}
                          </Typography>
                        )}
                      </Stack>
                    }
                    key={i}
                  />
                ))}
              </Tabs>
            </Box>
            <Container
              sx={{
                paddingTop: 1,
                height: 1000,
                width: '1200px',
                overflow: 'auto',
                scrollbarGutter: 'stable',
              }}
            >
              {customQuestions && filteredCustomQuestions ? (
                cardView === 'list' ? (
                  <CQTable
                    customQuestions={filteredCustomQuestions}
                    handleDetailsClick={handleDetailsClick}
                    handleEditClick={handleEditClick}
                    questionStatus={questionStatus}
                  />
                ) : (
                  <Stack
                    direction={'row'}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    {filteredCustomQuestions.length ? (
                      filteredCustomQuestions.map((q) => (
                        <Card
                          variant="outlined"
                          sx={{ width: '500px', margin: 2 }}
                          onClick={() => handleCardClick(q)}
                        >
                          <CardActionArea>
                            {customQuestionCard(q)}
                          </CardActionArea>
                        </Card>
                      ))
                    ) : (
                      <Card variant="outlined" sx={{ marginTop: 2 }}>
                        No questions
                      </Card>
                    )}
                  </Stack>
                )
              ) : null}
            </Container>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
};
